import { create } from 'zustand';

import type { ParticipantId } from './types';

export type WorkspaceTool = 'pointer' | 'hand' | 'card' | 'cluster' | 'text' | 'line' | 'shape';
export type WorkspaceOverlay = 'share' | 'feedback' | 'theme' | 'import' | 'password' | null;

interface WorkspaceUiState {
  selectedTool: WorkspaceTool;
  selectedFontStyle: string;
  selectedFontSize: number;
  selectedFontColor: string;
  selectedCardColor: string;
  selectedCardShape: string;
  selectedLineColor: string;
  selectedLineThickness: number;
  selectedShape: string;
  selectedPenColor: string;
  selectedPenThickness: number;
  selectedFillColor: string;
  selectedFillOpacity: number;
  participantPanelOpen: boolean;
  actionMenuOpen: boolean;
  activeOverlay: WorkspaceOverlay;
  visibleParticipantIds: ParticipantId[];
  zoom: number;
}

interface WorkspaceUiActions {
  setSelectedTool: (selectedTool: WorkspaceTool) => void;
  setSelectedCardColor: (selectedCardColor: string) => void;
  setSelectedCardShape: (selectedCardShape: string) => void;
  setSelectedFontStyle: (selectedFontStyle: string) => void;
  setSelectedFontSize: (selectedFontSize: number) => void;
  setSelectedFontColor: (selectedFontColor: string) => void;
  setSelectedLineColor: (selectedLineColor: string) => void;
  setSelectedLineThickness: (selectedLineThickness: number) => void;
  setSelectedShape: (selectedShape: string) => void;
  setSelectedPenColor: (selectedPenColor: string) => void;
  setSelectedPenThickness: (selectedPenThickness: number) => void;
  setSelectedFillColor: (selectedFillColor: string) => void;
  setSelectedFillOpacity: (selectedFillOpacity: number) => void;
  toggleParticipantPanel: () => void;
  setActionMenuOpen: (actionMenuOpen: boolean) => void;
  setActiveOverlay: (activeOverlay: WorkspaceOverlay) => void;
  toggleVisibleParticipant: (participantId: ParticipantId) => void;
  toggleVisibleParticipants: (participantIds: ParticipantId[]) => void;
  clearVisibleParticipants: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
  resetWorkspaceUi: () => void;
}

export type WorkspaceUiStore = WorkspaceUiState & WorkspaceUiActions;

const initialState: WorkspaceUiState = {
  selectedTool: 'pointer',
  selectedFontStyle: 'Arial,Arial,Helvetica,sans-serif',
  selectedFontSize: 18,
  selectedFontColor: '#000000',
  selectedCardColor: '#FFFF99',
  selectedCardShape: 'card',
  selectedLineColor: '#000000',
  selectedLineThickness: 1,
  selectedShape: 'rectangle',
  selectedPenColor: '#000000',
  selectedPenThickness: 1,
  selectedFillColor: '#FF0000',
  selectedFillOpacity: 100,
  participantPanelOpen: true,
  actionMenuOpen: false,
  activeOverlay: null,
  visibleParticipantIds: [],
  zoom: 1,
};

export const useWorkspaceUiStore = create<WorkspaceUiStore>((set) => ({
  ...initialState,
  setSelectedTool: (selectedTool) => set({ selectedTool }),
  setSelectedCardColor: (selectedCardColor) => set({ selectedCardColor }),
  setSelectedCardShape: (selectedCardShape) => set({ selectedCardShape }),
  setSelectedFontStyle: (selectedFontStyle) => set({ selectedFontStyle }),
  setSelectedFontSize: (selectedFontSize) => set({ selectedFontSize }),
  setSelectedFontColor: (selectedFontColor) => set({ selectedFontColor }),
  setSelectedLineColor: (selectedLineColor) => set({ selectedLineColor }),
  setSelectedLineThickness: (selectedLineThickness) => set({ selectedLineThickness }),
  setSelectedShape: (selectedShape) => set({ selectedShape }),
  setSelectedPenColor: (selectedPenColor) => set({ selectedPenColor }),
  setSelectedPenThickness: (selectedPenThickness) => set({ selectedPenThickness }),
  setSelectedFillColor: (selectedFillColor) => set({ selectedFillColor }),
  setSelectedFillOpacity: (selectedFillOpacity) => set({ selectedFillOpacity }),
  toggleParticipantPanel: () =>
    set((state) => ({ participantPanelOpen: !state.participantPanelOpen })),
  setActionMenuOpen: (actionMenuOpen) => set({ actionMenuOpen }),
  setActiveOverlay: (activeOverlay) => set({ activeOverlay }),
  toggleVisibleParticipant: (participantId) =>
    set((state) => ({
      visibleParticipantIds: state.visibleParticipantIds.includes(participantId)
        ? state.visibleParticipantIds.filter((selectedId) => selectedId !== participantId)
        : [...state.visibleParticipantIds, participantId],
    })),
  toggleVisibleParticipants: (participantIds) =>
    set((state) => {
      const uniqueParticipantIds = Array.from(new Set(participantIds));
      const allSelected =
        uniqueParticipantIds.length > 0 &&
        uniqueParticipantIds.every((participantId) =>
          state.visibleParticipantIds.includes(participantId)
        );

      return {
        visibleParticipantIds: allSelected
          ? state.visibleParticipantIds.filter(
              (selectedId) => !uniqueParticipantIds.includes(selectedId)
            )
          : Array.from(new Set([...state.visibleParticipantIds, ...uniqueParticipantIds])),
      };
    }),
  clearVisibleParticipants: () => set({ visibleParticipantIds: [] }),
  zoomIn: () => set((state) => ({ zoom: Math.min(2, Math.round((state.zoom + 0.1) * 10) / 10) })),
  zoomOut: () =>
    set((state) => ({ zoom: Math.max(0.1, Math.round((state.zoom - 0.1) * 10) / 10) })),
  resetWorkspaceUi: () => set(initialState),
}));
